import type { DataProvidersModel } from '../DataProvider/DataProvider.types';
import SOURCE_TYPE from '../SourceCreate.constants';
import { SourceDialogMode } from '../SourceCreate.types';
import type { CategorizedKeys } from './SourceCreateDialog.types';

/**
 * Builds a standardized source name following the naming convention.
 *
 * Format: {orgLabel}-{sourceType}-{apiEndpoint}-{siteName}
 * Examples:
 * - "ACME-MicrosoftEntraID-Graph-HQ"
 * - "ACME-AWS-Site1"
 * - "ACME-O365-Marketing"
 *
 * @param orgLabel - Organization identifier/label
 * @param sourceTypeName - Name of the source type
 * @param sourceTypeId - ID of the source type
 * @param siteName - Optional site name
 * @param selectedSubSourceType - Sub-source type (for Microsoft Entra ID)
 * @returns Formatted source name
 */
export const buildSourceName = (
  orgLabel: string,
  sourceTypeName: string | undefined,
  sourceTypeId: number,
  siteName: string | undefined,
  selectedSubSourceType: string
): string => {
  const typeName = sourceTypeName?.replace(/\s+/g, '') ?? '';
  const cleanSiteName = siteName?.replace(/\s+/g, '') ?? '';
  const siteNameSuffix = cleanSiteName ? `-${cleanSiteName}` : '';

  // Microsoft Entra ID sources need API endpoint type in the name
  let apiEndpoint = '';
  if (Number(sourceTypeId) === SOURCE_TYPE.MICROSOFT_ENTRA_ID) {
    apiEndpoint = selectedSubSourceType === 'Graph API' ? 'Graph' : 'O365';
  }

  const apiEndpointName = apiEndpoint ? `-${apiEndpoint}` : '';

  return `${orgLabel}-${typeName}${apiEndpointName}${siteNameSuffix}`;
};

/**
 * Categorizes Medigate alert device fields by their category.
 *
 * Business Rule: Medigate alert fields are submitted as nested objects
 * grouped by category (e.g., alert_device_fields, alert_fields).
 *
 * Input: [{ key: 'Auth', category: 'alert_device_fields' }, ...]
 * Output: { alert_device_fields: ['Auth', ...], alert_fields: [...] }
 *
 * @param alertFields - Array of alert device fields with keys and categories
 * @returns Object with keys grouped by category
 */
export const categorizeAlertFields = (
  alertFields: Array<{ key: string; category: string }>
): CategorizedKeys => {
  return alertFields.reduce((acc: CategorizedKeys, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item.key);
    return acc;
  }, {} as CategorizedKeys);
};

/**
 * Determines the dialog title based on mode and current step.
 *
 * Title Logic:
 * - Edit mode: "Edit Source ({sourceName})"
 * - Delete mode: "Delete Source"
 * - AddThumbPrintId/AddAccessCode mode: "Action Required {sourceName}"
 * - New mode: Step-specific titles or generic create title
 *
 * @param mode - Dialog mode ('New', 'Edit', 'Delete', 'AddThumbPrintId', 'AddAccessCode')
 * @param selectedCloudSource - Currently selected cloud source (for Edit mode)
 * @param sourceResponse - Source response (for action required modes)
 * @param currentStep - Current wizard step (for New mode)
 * @param orgId - Organization ID (for New mode)
 * @returns Appropriate dialog title
 */
export const determineDialogTitle = (
  mode: string,
  selectedCloudSource: { source_name?: string } | null,
  sourceResponse: { source_name?: string } | null,
  currentStep: number,
  orgId: number
): string => {
  if (mode === SourceDialogMode.New) {
    if (orgId === 0) {
      return 'Create New Source';
    }
    return currentStep === 1
      ? 'Create New Source - Step 1: Select Source'
      : 'Create New Source - Step 2: Configure Source';
  }

  if (mode === SourceDialogMode.Delete) {
    return 'Delete Source';
  }

  if (mode === SourceDialogMode.Edit && selectedCloudSource) {
    return `Edit Source (${selectedCloudSource.source_name || ''})`;
  }

  if (
    mode === SourceDialogMode.AddThumbPrintId ||
    mode === SourceDialogMode.AddAccessCode
  ) {
    return `Action Required ${sourceResponse?.source_name || ''}`;
  }

  return '';
};

/**
 * Checks if the source type requires data provider selection validation.
 *
 * Business Rule: AWS S3, Google Storage, and Microsoft sources require
 * at least one data provider to be selected.
 *
 * @param sourceTypeId - ID of the source type
 * @returns True if source type requires data provider validation
 */
export const requiresDataProviderValidation = (
  sourceTypeId: number
): boolean => {
  return (
    sourceTypeId === SOURCE_TYPE.AWS_S3 ||
    sourceTypeId === SOURCE_TYPE.GOOGLE_STORAGE ||
    sourceTypeId === SOURCE_TYPE.MICROSOFT
  );
};

/**
 * Validates AWS S3 data provider selection.
 *
 * @param dataProvider - Data provider configuration object
 * @returns True if at least one AWS S3 provider is selected
 */
export const isAwsS3ProviderSelected = (
  dataProvider: DataProvidersModel | undefined
): boolean => {
  if (!dataProvider) return false;

  return (
    !!dataProvider?.CiscoUmbrella?.selected ||
    !!dataProvider?.AWSGuardDuty?.selected ||
    !!dataProvider?.DNS_Filter?.selected ||
    !!dataProvider?.AWS_CloudTrail?.selected
  );
};

/**
 * Validates Google Cloud Storage data provider selection.
 *
 * @param dataProvider - Data provider configuration object
 * @returns True if at least one GCS provider is selected
 */
export const isGoogleStorageProviderSelected = (
  dataProvider: DataProvidersModel | undefined
): boolean => {
  if (!dataProvider) return false;

  return (
    !!dataProvider?.Firewall?.selected ||
    !!dataProvider?.DNS?.selected ||
    !!dataProvider?.VPC?.selected ||
    !!dataProvider?.Audit?.selected ||
    !!dataProvider?.CustomLogs?.selected
  );
};

/**
 * Validates Microsoft data provider selection.
 *
 * In Edit mode, also checks for previously saved data providers
 * which may be hidden/disabled in the UI but still count as selected.
 *
 * @param dataProvider - Data provider configuration object
 * @param savedProviders - Previously saved provider names (Edit mode only)
 * @returns True if at least one Microsoft provider is selected
 */
export const isMicrosoftProviderSelected = (
  dataProvider: DataProvidersModel | undefined,
  savedProviders: string[]
): boolean => {
  if (!dataProvider) return savedProviders.length > 0;

  const hasSelectedProvider =
    !!dataProvider?.O365?.selected ||
    !!dataProvider?.MicrosoftDefenderUnified?.selected ||
    !!dataProvider?.MicrosoftEntraID?.GraphAPI?.selected ||
    !!dataProvider?.MicrosoftEntraID?.O365API?.selected;

  return hasSelectedProvider || savedProviders.length > 0;
};

/**
 * Determines the button label based on dialog mode and wizard step.
 *
 * Business rule:
 * - Edit mode always shows "Save"
 * - New mode shows "Next" on source selection step, "Create" on configuration step
 * - Other modes show "Submit"
 *
 * @param mode - The dialog mode (New, Edit, Delete, AddThumbPrintId, AddAccessCode)
 * @param currentStep - Current step in wizard (for New mode)
 * @param WIZARD_STEP - Wizard step constants object
 * @returns The appropriate button label
 */
export const determineButtonLabel = (
  mode: string,
  currentStep: number,
  WIZARD_STEP: { SELECT_SOURCE: number; CONFIGURE_SOURCE: number }
): string => {
  if (mode === SourceDialogMode.Edit) return 'Save';
  if (mode === SourceDialogMode.New) {
    if (currentStep === WIZARD_STEP.SELECT_SOURCE) return 'Next';
    return 'Create';
  }
  return 'Submit';
};

/**
 * Builds the setup instructions documentation URL for a specific source type.
 *
 * Looks up the source type in CloudAPILinks and constructs the full documentation URL.
 * Returns null if the source type doesn't have documentation or isn't found.
 *
 * @param selectedSourceTypeId - ID of the selected source type
 * @param originalSourceTypes - Array of all available source types
 * @param DOCS_URL - Base URL for documentation
 * @param CloudAPILinks - Mapping of source type names to documentation paths
 * @returns Full documentation URL or null
 */
export const buildSetupInstructionsUrl = (
  selectedSourceTypeId: number,
  originalSourceTypes: Array<{ id: number; type?: string }>,
  DOCS_URL: string,
  CloudAPILinks: Record<string, string>
): string | null => {
  if (
    selectedSourceTypeId > 0 &&
    originalSourceTypes &&
    originalSourceTypes.length > 0
  ) {
    const srcType = originalSourceTypes.find(
      (item: { id: number; type?: string }) => item.id === selectedSourceTypeId
    )?.type;

    if (!srcType) {
      return null;
    }

    const sourceTypeName = srcType.toLowerCase();
    if (Object.hasOwn(CloudAPILinks, sourceTypeName)) {
      return `${DOCS_URL}${CloudAPILinks[sourceTypeName as keyof typeof CloudAPILinks]}`;
    }
  }
  return null;
};

/**
 * Checks if a source type is supported for Auto-SOC onboarding.
 *
 * Business rule: Only source types present in the tieredSourceTypes collection
 * are supported for Auto-SOC. If a source type is selected but not in this list,
 * we show a warning that it can still be configured manually.
 *
 * @param selectedSourceTypeId - ID of the selected source type
 * @param tieredSourceTypes - Array of source types supported for Auto-SOC
 * @returns True if source is NOT supported for Auto-SOC
 */
export const isAutoSocUnsupportedSource = (
  selectedSourceTypeId: number,
  tieredSourceTypes: Array<{ id: number }>
): boolean => {
  if (selectedSourceTypeId > 0 && tieredSourceTypes.length > 0) {
    const selectedType = tieredSourceTypes.find(
      (type: { id: number }) => type.id === selectedSourceTypeId
    );
    return !selectedType; // Return true if NOT found (unsupported)
  }
  return false;
};
